import { Link, useSearchParams } from 'react-router-dom';
import { useFailedSessions } from '@/hooks/useConsoleData';
import { ago, when } from '@/lib/format';
import { durationLabel, endingLine, newestFirst, tracePlatformLabel, type TraceFeedRow } from '@/lib/session-data';
import s from './console.module.css';
import x from './session.module.css';

/**
 * Sessions that did not finish: every trace whose ending is not completed
 * in the window, across people, newest first. On Today for the last day;
 * on its own page with a wider window. Each row names the person, what
 * ended how, and opens the two-lane page. Read every morning (section 4,
 * week 3): the page's own gaps are the next PRs.
 */
export function FailedSessionRow({ t }: { t: TraceFeedRow }) {
  const who = t.person ? (t.person.name || t.person.email) : 'account not found';
  return (
    <div className={x.failedRow} data-failed={t.peerId}>
      <div className={x.failedHead}>
        <span className={s.jTime}>{when(t.startedAt)}</span>
        {t.person ? <Link className={x.failedWho} to={`/people/${encodeURIComponent(t.person.userId)}`}>{who}</Link> : <span className={x.failedWho}>{who}</span>}
        <span className={x.failedWhat}>{endingLine(t)}</span>
        <Link className={s.linkA} to={`/people/${encodeURIComponent(t.userId)}/sessions/${encodeURIComponent(t.peerId)}`}>Open the session</Link>
      </div>
      <div className={x.failedMeta}>
        {ago(t.startedAt)} · {durationLabel(t.durationS)} · {tracePlatformLabel(t.platform)} {t.appVersion ?? ''} · {t.engineBase ?? 'engine host not recorded'} · {t.entryCount} entries{t.truncated ? ' (truncated)' : ''}{t.result ? ' · a result row exists' : ' · no result row'}
      </div>
    </div>
  );
}

export function FailedSessionsList({ since }: { since: string }) {
  const q = useFailedSessions(since);
  const rows = q.data ? newestFirst(q.data.traces) : [];
  return (
    <>
      {q.isError && <div className={s.error}>Could not load the failed sessions. This list needs GET /provider/admin/sessions/failed.</div>}
      {q.isLoading && <div className={s.sub}>Loading the sessions.</div>}
      {q.data && rows.length === 0 && <div className={s.empty}>Every session that posted a trace since {when(q.data.since)} finished.</div>}
      <div className={s.list} data-failed-list>
        {rows.map((t) => <FailedSessionRow key={t.id} t={t} />)}
      </div>
      {q.data && <div className={s.sub} style={{ marginTop: 8 }}>Window {when(q.data.since)} to {when(q.data.until)}, {rows.length} of at most {q.data.limit}. Only clients that post a trace are here (web since 2026-10-11; the phones follow), so a session whose tab died mid-check is still missing.</div>}
    </>
  );
}

const WINDOWS: { key: string; label: string }[] = [
  { key: '24h', label: 'Since yesterday' },
  { key: '48h', label: 'Two days' },
  { key: '7d', label: 'A week' },
  { key: '30d', label: 'Thirty days' },
];

/** The window lives in the URL (?since=7d) so a morning read can be sent as a link. */
export default function FailedSessionsPage() {
  const [params, setParams] = useSearchParams();
  const since = WINDOWS.some((w) => w.key === params.get('since')) ? (params.get('since') as string) : '24h';
  return (
    <div className={s.stack}>
      <div className={s.pageHead}>
        <div>
          <div className={s.sub}><Link to="/">Today</Link> · sessions that did not finish</div>
          <h1 className={s.pageTitle}>Sessions that did not finish</h1>
        </div>
        <div className={s.filters}>
          {WINDOWS.map((w) => <button key={w.key} type="button" className={`${s.filter} ${since === w.key ? s.filterOn : ''}`} onClick={() => setParams({ since: w.key }, { replace: true })}>{w.label}</button>)}
        </div>
      </div>
      <FailedSessionsList since={since} />
    </div>
  );
}
